import { useEffect, useRef, useState } from 'react';
import { Box, Button, Link, TextField, Typography } from '@mui/material';

import B3Request from '@/shared/service/request/b3Fetch';

interface LinkItem {
  entityId: number;
  name: string;
  path: string;
}

const GET_CATEGORIES = `
  query GetFooterCategories {
    site {
      categoryTree {
        entityId
        name
        path
      }
    }
  }
`;

// Use inline fragments per page type; include isVisibleInNavigation to exclude B2B internal pages.
const GET_PAGES = `
  query GetFooterPages {
    site {
      content {
        pages(first: 50) {
          edges {
            node {
              ... on NormalPage {
                entityId
                name
                path
                isVisibleInNavigation
              }
              ... on ContactPage {
                entityId
                name
                path
                isVisibleInNavigation
              }
              ... on RawHtmlPage {
                entityId
                name
                path
                isVisibleInNavigation
              }
              ... on BlogIndexPage {
                entityId
                name
                path
                isVisibleInNavigation
              }
            }
          }
        }
      }
    }
  }
`;

const GET_BRANDS = `
  query GetFooterBrands {
    site {
      brands(first: 6) {
        edges {
          node {
            entityId
            name
            path
          }
        }
      }
    }
  }
`;

const GET_STORE_SETTINGS = `
  query GetStoreSettings {
    site {
      settings {
        storeName
        contact {
          city
          state
        }
      }
    }
  }
`;

const NAVIGATE_LINKS = [
  { name: 'Shipping & Returns', path: '/shipping-returns/' },
  { name: 'Contact Us', path: '/contact-us/' },
  { name: 'Blog', path: '/blog/' },
  { name: 'Sitemap', path: '/sitemap.php' },
];

export default function StorefrontFooter() {
  const [categories, setCategories] = useState<LinkItem[]>([]);
  const [pages, setPages] = useState<LinkItem[]>([]);
  const [brands, setBrands] = useState<LinkItem[]>([]);
  const [storeName, setStoreName] = useState('');
  const [storeLocation, setStoreLocation] = useState('');
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const hasFetched = useRef(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    B3Request.graphqlBCProxy<{ data?: { site?: { categoryTree?: LinkItem[] } } }>({
      query: GET_CATEGORIES,
    })
      .then((res) => setCategories(res?.data?.site?.categoryTree ?? []))
      .catch(() => {});

    B3Request.graphqlBCProxy<{
      data?: {
        site?: {
          content?: {
            pages?: {
              edges?: Array<{ node: Partial<LinkItem> & { isVisibleInNavigation?: boolean } }>;
            };
          };
        };
      };
    }>({ query: GET_PAGES })
      .then((res) => {
        const raw = (res?.data?.site?.content?.pages?.edges ?? []).map((e) => e.node);
        const valid = raw.filter(
          (node): node is LinkItem =>
            !!node.entityId && !!node.name && !!node.path && node.isVisibleInNavigation !== false,
        );
        const orderedPages = NAVIGATE_LINKS.map((link, index) => {
          const page = valid.find(
            (item) =>
              item.name.toLowerCase() === link.name.toLowerCase() || item.path === link.path,
          );

          return page ?? { entityId: -index - 1, ...link };
        });

        setPages(orderedPages);
      })
      .catch(() => {});

    B3Request.graphqlBCProxy<{
      data?: { site?: { brands?: { edges?: Array<{ node: LinkItem }> } } };
    }>({ query: GET_BRANDS })
      .then((res) => setBrands((res?.data?.site?.brands?.edges ?? []).map((e) => e.node)))
      .catch(() => {});

    B3Request.graphqlBCProxy<{
      data?: {
        site?: {
          settings?: {
            storeName?: string;
            contact?: { city?: string; state?: string };
          };
        };
      };
    }>({ query: GET_STORE_SETTINGS })
      .then((res) => {
        const settings = res?.data?.site?.settings;
        setStoreName(settings?.storeName ?? '');

        const city = settings?.contact?.city;
        const state = settings?.contact?.state;

        if (city || state) {
          setStoreLocation([city, state].filter(Boolean).join(', '));
        }
      })
      .catch(() => {});
  }, []);

  const handleSubscribe = async () => {
    if (!email) return;

    try {
      const formData = new FormData();
      formData.append('nl_email', email);
      await fetch('/subscribe.php', { method: 'POST', body: formData });
      setSubscribeStatus('success');
      setEmail('');
    } catch {
      setSubscribeStatus('error');
    }
  };

  const linkSx = {
    display: 'block',
    color: '#4f4f4f',
    fontSize: '11px',
    lineHeight: 1.45,
    mb: '2px',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  };

  const headingSx = {
    fontSize: '12px',
    fontWeight: 400,
    color: '#2f3440',
    mb: '10px',
  };

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        borderTop: '1px solid #d9d9d9',
        mt: '40px',
        pt: '34px',
        pb: '28px',
        px: { xs: '24px', md: '52px' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: '92px 92px 96px 88px 300px',
          },
          columnGap: { xs: '24px', md: '50px' },
          rowGap: '28px',
          maxWidth: '920px',
          mx: 'auto',
        }}
      >
        <Box>
          <Typography sx={headingSx}>Navigate</Typography>
          {pages.map((page) => (
            <Link key={page.entityId} href={page.path} sx={linkSx}>
              {page.name}
            </Link>
          ))}
          <Box sx={{ mt: '60px' }}>
            <Typography sx={{ fontSize: '10px', lineHeight: 1.45, color: '#69717c' }}>
              Powered by{' '}
              <Link
                href="https://www.bigcommerce.com"
                target="_blank"
                sx={{
                  color: '#69717c',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                BigCommerce
              </Link>
            </Typography>
            {storeName && (
              <Typography sx={{ fontSize: '10px', lineHeight: 1.45, color: '#69717c' }}>
                &copy; {currentYear} {storeName}
              </Typography>
            )}
          </Box>
        </Box>

        <Box>
          <Typography sx={headingSx}>Categories</Typography>
          {categories.map((category) => (
            <Link key={category.entityId} href={category.path} sx={linkSx}>
              {category.name}
            </Link>
          ))}
        </Box>

        <Box>
          <Typography sx={headingSx}>Popular Brands</Typography>
          {brands.map((brand) => (
            <Link key={brand.entityId} href={brand.path} sx={linkSx}>
              {brand.name}
            </Link>
          ))}
          {brands.length > 0 && (
            <Link href="/brands/" sx={linkSx}>
              View All
            </Link>
          )}
        </Box>

        <Box>
          <Typography sx={headingSx}>Info</Typography>
          {storeLocation && (
            <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: '#4f4f4f' }}>
              {storeLocation}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={headingSx}>Subscribe to our newsletter</Typography>
          <Typography sx={{ fontSize: '11px', color: '#69717c', mb: '18px', lineHeight: 1.45 }}>
            Get the latest updates on new products and upcoming sales
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleSubscribe()}
              sx={{
                width: '158px',
                backgroundColor: '#fff',
                '& .MuiInputBase-input': {
                  fontSize: '11px',
                  height: '16px',
                  py: '6px',
                  px: '10px',
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSubscribe}
              sx={{
                minWidth: '102px',
                height: '31px',
                backgroundColor: '#3f3f3f !important',
                color: '#ffffff !important',
                borderRadius: '2px',
                boxShadow: 'none',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2f2f2f !important', boxShadow: 'none' },
              }}
            >
              Subscribe
            </Button>
          </Box>
          {subscribeStatus === 'success' && (
            <Typography sx={{ fontSize: '13px', color: 'green', mt: '8px' }}>
              Thank you for subscribing!
            </Typography>
          )}
          {subscribeStatus === 'error' && (
            <Typography sx={{ fontSize: '13px', color: 'red', mt: '8px' }}>
              Something went wrong. Please try again.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
